import { createClientLogger } from '@/client/logging/client-logger';
import { dispatchBrowserIncident } from '@/client/observability/browser-incident-dispatcher';
import { AppError, normalizeUnknownError } from '@/shared/errors/app-error';
import { isRequestCanceled } from '@/shared/errors/is-request-canceled';
import { sanitizeForLog } from '@/shared/logging/sanitize-log';
import { buildIncidentFingerprint, classifyIncident } from '@/shared/observability/incident-policy';
import { releaseManifest } from '@/shared/release-manifest';

export type ClientRuntimeBoundary = 'route' | 'global';

export type ClientRuntimeIncident = Readonly<{
    boundary: ClientRuntimeBoundary;
    failureId: string;
    time: string;
    route: string;
    appRevision: string;
    code: string;
    category: string;
    capability: string | null;
    layer: string | null;
    operation: string | null;
    boundaryDisposition: 'terminal' | 'rethrow' | null;
    summary: string;
    retryable: boolean;
    requestId: string | null;
    correlationId: string | null;
    operationId: string | null;
    digest: string | null;
}>;

const DEFAULT_CLIENT_RUNTIME_CODE = 'ClientRenderError';
const DEFAULT_CLIENT_RUNTIME_CATEGORY = 'render';
const DEFAULT_CLIENT_RUNTIME_SUMMARY = 'Не удалось безопасно отобразить страницу.';
const DEFAULT_CLIENT_RUNTIME_CAPABILITY = 'runtime-observability';
const DEFAULT_CLIENT_RUNTIME_LAYER = 'terminal';
const DEFAULT_CLIENT_RUNTIME_CANCELED_CODE = 'ERR_CANCELED';
const DEFAULT_CLIENT_RUNTIME_BOUNDARY_DISPOSITION = 'terminal' as const;
const DEFAULT_CLIENT_RUNTIME_CLASSIFICATION = 'incident' as const;
const clientRuntimeLogger = createClientLogger({ scope: 'runtime-failure' });

const readOptionalString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
};

const sanitizeSummary = (value: string | null | undefined): string => {
    const sanitized = sanitizeForLog(value ?? DEFAULT_CLIENT_RUNTIME_SUMMARY, {
        redactSensitiveKeys: false,
    });
    if (typeof sanitized !== 'string') {
        return DEFAULT_CLIENT_RUNTIME_SUMMARY;
    }

    const normalized = sanitized.trim();
    return normalized ? normalized : DEFAULT_CLIENT_RUNTIME_SUMMARY;
};

const toCanonicalPrefixToken = (value: string | null | undefined, fallback: string): string => {
    const normalized = (value ?? fallback)
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();

    return normalized || fallback;
};

const buildCanonicalCanceledSummary = ({
    capability,
    operation,
}: {
    capability: string | null;
    operation: string | null;
}) => {
    return `[client.${toCanonicalPrefixToken(
        capability,
        DEFAULT_CLIENT_RUNTIME_CAPABILITY,
    )}.${toCanonicalPrefixToken(operation, 'request-cancel')}] Client runtime work was canceled.`;
};

const readRawErrorMessage = (error: unknown): string | null => {
    if (error instanceof Error) {
        return readOptionalString(error.message);
    }

    if (typeof error === 'object' && error !== null && 'message' in error) {
        return readOptionalString((error as { message?: unknown }).message);
    }

    return null;
};

const readRawErrorName = (error: unknown): string | null => {
    if (error instanceof Error) {
        return readOptionalString(error.name);
    }

    if (typeof error === 'object' && error !== null && 'name' in error) {
        return readOptionalString((error as { name?: unknown }).name);
    }

    return null;
};

const toSafeRoutePath = (value: string): string => {
    const normalized = value.trim();
    if (!normalized) {
        return '/';
    }

    if (/^https?:\/\//i.test(normalized)) {
        try {
            return new URL(normalized).pathname || '/';
        } catch {
            const sanitized = sanitizeForLog(normalized.split(/[?#]/, 1)[0], {
                redactSensitiveKeys: false,
            });
            return typeof sanitized === 'string' ? sanitized : '/';
        }
    }

    if (normalized.startsWith('/')) {
        return normalized.split(/[?#]/, 1)[0] || '/';
    }

    const sanitized = sanitizeForLog(normalized, {
        redactSensitiveKeys: false,
    });

    if (typeof sanitized !== 'string') {
        return normalized.split(/[?#]/, 1)[0] || normalized;
    }

    return sanitized.split(/[?#]/, 1)[0] || normalized;
};

const normalizeClientRuntimeRoute = (route: string | null | undefined): string => {
    if (route?.trim()) {
        return toSafeRoutePath(route);
    }

    if (typeof window === 'undefined') {
        return 'unknown';
    }

    return window.location.pathname || '/';
};

const isChunkLoadError = (error: AppError): boolean => {
    return (
        error.originalName === 'ChunkLoadError' ||
        /ChunkLoadError/i.test(error.summary) ||
        /Loading chunk .* failed/i.test(error.summary)
    );
};

const createFailureId = (digest: string | null, now: Date): string => {
    if (digest) {
        return `fail_digest_${digest}`;
    }

    const uuidSegment =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID().slice(0, 8)
            : Math.random().toString(36).slice(2, 10);

    return `fail_${now.getTime()}_${uuidSegment}`;
};

const classifyClientRuntimeError = (error: AppError) => {
    if (isRequestCanceled(error) || error.category === 'canceled') {
        const capability = readOptionalString(error.capability) ?? DEFAULT_CLIENT_RUNTIME_CAPABILITY;
        const operation = readOptionalString(error.operation);

        return {
            code:
                readOptionalString(error.code) && error.code !== DEFAULT_CLIENT_RUNTIME_CODE
                    ? error.code
                    : DEFAULT_CLIENT_RUNTIME_CANCELED_CODE,
            category: 'canceled',
            capability,
            layer: readOptionalString(error.layer) ?? DEFAULT_CLIENT_RUNTIME_LAYER,
            operation,
            boundaryDisposition: error.boundaryDisposition ?? DEFAULT_CLIENT_RUNTIME_BOUNDARY_DISPOSITION,
            summary: buildCanonicalCanceledSummary({
                capability,
                operation,
            }),
            retryable: error.retryable ?? true,
            requestId: readOptionalString(error.requestId),
            correlationId: readOptionalString(error.correlationId) ?? readOptionalString(error.requestId),
            operationId: readOptionalString(error.operationId),
        };
    }

    if (isChunkLoadError(error)) {
        return {
            code: error.originalName || 'ChunkLoadError',
            category: 'asset_loading',
            capability: error.capability ?? DEFAULT_CLIENT_RUNTIME_CAPABILITY,
            layer: error.layer ?? DEFAULT_CLIENT_RUNTIME_LAYER,
            operation:
                error.operation ??
                (error.route ? `client_${error.route.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_boundary` : null),
            boundaryDisposition: error.boundaryDisposition ?? DEFAULT_CLIENT_RUNTIME_BOUNDARY_DISPOSITION,
            summary: 'Клиентский код страницы загрузился не полностью.',
            retryable: true,
            requestId: null,
            correlationId: null,
            operationId: null,
        };
    }

    return {
        code: error.code || DEFAULT_CLIENT_RUNTIME_CODE,
        category: readOptionalString(error.category) ?? DEFAULT_CLIENT_RUNTIME_CATEGORY,
        capability: readOptionalString(error.capability) ?? DEFAULT_CLIENT_RUNTIME_CAPABILITY,
        layer: readOptionalString(error.layer) ?? DEFAULT_CLIENT_RUNTIME_LAYER,
        operation: readOptionalString(error.operation),
        boundaryDisposition: error.boundaryDisposition ?? DEFAULT_CLIENT_RUNTIME_BOUNDARY_DISPOSITION,
        summary: sanitizeSummary(error.summary),
        retryable: error.retryable ?? true,
        requestId: readOptionalString(error.requestId),
        correlationId: readOptionalString(error.correlationId) ?? readOptionalString(error.requestId),
        operationId: readOptionalString(error.operationId),
    };
};

export const normalizeClientRuntimeIncident = ({
    error,
    boundary,
    route,
    now = new Date(),
}: {
    error: unknown;
    boundary: ClientRuntimeBoundary;
    route?: string | null;
    now?: Date;
}): ClientRuntimeIncident => {
    const normalizedError = normalizeUnknownError(error, {
        defaultCode: DEFAULT_CLIENT_RUNTIME_CODE,
        defaultSummary: 'Unknown client runtime failure',
        defaultCategory: DEFAULT_CLIENT_RUNTIME_CATEGORY,
        capability: DEFAULT_CLIENT_RUNTIME_CAPABILITY,
        layer: DEFAULT_CLIENT_RUNTIME_LAYER,
        operation: boundary === 'route' ? 'client_route_boundary' : 'client_global_boundary',
        boundaryDisposition: DEFAULT_CLIENT_RUNTIME_BOUNDARY_DISPOSITION,
        severity: 'error',
        retryable: true,
    });
    const classification = classifyClientRuntimeError(normalizedError);
    const digest = readOptionalString((error as { digest?: unknown } | null | undefined)?.digest);

    return Object.freeze({
        boundary,
        failureId: createFailureId(digest, now),
        time: now.toISOString(),
        route: normalizeClientRuntimeRoute(route),
        appRevision: releaseManifest.revision,
        code: classification.code,
        category: classification.category,
        capability: classification.capability,
        layer: classification.layer,
        operation: classification.operation,
        boundaryDisposition: classification.boundaryDisposition,
        summary: classification.summary,
        retryable: classification.retryable,
        requestId: classification.requestId,
        correlationId: classification.correlationId,
        operationId: classification.operationId,
        digest,
    });
};

export const buildClientRuntimeSupportPayload = (incident: ClientRuntimeIncident): string => {
    const classification = DEFAULT_CLIENT_RUNTIME_CLASSIFICATION;
    const fingerprint = buildIncidentFingerprint(
        'client_runtime',
        incident.code,
        incident.category,
        incident.route,
        incident.boundary,
        incident.digest,
    );
    const lines = [
        `failureId: ${incident.failureId}`,
        ...(incident.requestId ? [`requestId: ${incident.requestId}`] : []),
        ...(incident.correlationId && incident.correlationId !== incident.requestId
            ? [`correlationId: ${incident.correlationId}`]
            : []),
        ...(incident.operationId ? [`operationId: ${incident.operationId}`] : []),
        `time: ${incident.time}`,
        `route: ${incident.route}`,
        `boundary: ${incident.boundary}`,
        `appRevision: ${incident.appRevision}`,
        `code: ${incident.code}`,
        `category: ${incident.category}`,
        ...(incident.capability ? [`capability: ${incident.capability}`] : []),
        ...(incident.layer ? [`layer: ${incident.layer}`] : []),
        ...(incident.operation ? [`operation: ${incident.operation}`] : []),
        ...(incident.boundaryDisposition ? [`boundaryDisposition: ${incident.boundaryDisposition}`] : []),
        `classification: ${classification}`,
        `retryable: ${String(incident.retryable)}`,
        ...(incident.digest ? [`digest: ${incident.digest}`] : []),
        ...(fingerprint ? [`fingerprint: ${fingerprint}`] : []),
    ];

    return lines.join('\n');
};

export const reportClientRuntimeIncident = ({
    incident,
    error,
}: {
    incident: ClientRuntimeIncident;
    error: unknown;
}) => {
    const rawErrorName = readRawErrorName(error);
    const rawErrorMessage = readRawErrorMessage(error);
    const normalizedError = normalizeUnknownError(error, {
        defaultCode: DEFAULT_CLIENT_RUNTIME_CODE,
        defaultSummary: 'Unknown client runtime failure',
        defaultCategory: DEFAULT_CLIENT_RUNTIME_CATEGORY,
        capability: incident.capability ?? DEFAULT_CLIENT_RUNTIME_CAPABILITY,
        layer: incident.layer ?? DEFAULT_CLIENT_RUNTIME_LAYER,
        operation:
            incident.operation ?? (incident.boundary === 'route' ? 'client_route_boundary' : 'client_global_boundary'),
        boundaryDisposition: incident.boundaryDisposition ?? DEFAULT_CLIENT_RUNTIME_BOUNDARY_DISPOSITION,
        severity: 'error',
        retryable: true,
    });
    const classification = classifyIncident({
        classification:
            incident.category === 'canceled' || incident.code === DEFAULT_CLIENT_RUNTIME_CANCELED_CODE
                ? null
                : DEFAULT_CLIENT_RUNTIME_CLASSIFICATION,
        category: incident.category,
        code: incident.code,
        error: normalizedError,
    });
    const fingerprint = buildIncidentFingerprint(
        'client_runtime',
        incident.code,
        incident.category,
        incident.route,
        incident.boundary,
        incident.digest,
    );
    const logLevel = classification === 'incident' ? 'error' : classification === 'canceled' ? 'info' : 'warn';
    const dispatchMetadata = {
        kind: 'client_runtime_boundary',
        failureId: incident.failureId,
        route: incident.route,
        boundary: incident.boundary,
        capability: incident.capability,
        layer: incident.layer,
        operation: incident.operation,
        boundaryDisposition: incident.boundaryDisposition,
        errorCode: incident.code,
        errorCategory: incident.category,
        classification,
        retryable: incident.retryable,
        requestId: incident.requestId,
        correlationId: incident.correlationId,
        operationId: incident.operationId,
        digest: incident.digest,
        appRevision: incident.appRevision,
        error: {
            name: rawErrorName ?? normalizedError.originalName ?? normalizedError.name,
            message:
                classification === 'canceled'
                    ? sanitizeSummary(rawErrorMessage ?? normalizedError.summary)
                    : sanitizeSummary(normalizedError.summary),
        },
    };

    clientRuntimeLogger[logLevel]('Client runtime boundary captured an error', {
        failureId: incident.failureId,
        boundary: incident.boundary,
        route: incident.route,
        capability: incident.capability,
        layer: incident.layer,
        operation: incident.operation,
        boundaryDisposition: incident.boundaryDisposition,
        errorCode: incident.code,
        errorCategory: incident.category,
        classification,
        fingerprint,
        retryable: incident.retryable,
        requestId: incident.requestId,
        correlationId: incident.correlationId,
        operationId: incident.operationId,
        digest: incident.digest,
        appRevision: incident.appRevision,
        error: dispatchMetadata.error,
    });

    void dispatchBrowserIncident({
        title: incident.summary,
        message: incident.summary,
        classification,
        errorCode: incident.code,
        errorCategory: incident.category,
        occurredAt: incident.time,
        fingerprint,
        requestId: incident.requestId,
        correlationId: incident.correlationId,
        operation: incident.operation,
        disposition: incident.boundaryDisposition ?? DEFAULT_CLIENT_RUNTIME_BOUNDARY_DISPOSITION,
        metadata: dispatchMetadata,
    }).catch(() => undefined);
};
